import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Joyride, EVENTS, STATUS } from "react-joyride";
import { useAuth, can } from "../auth";
import { useBranding } from "../context/BrandingContext";
import { useIsDesktop } from "../hooks/useMediaQuery";
import api from "../api";

// NOTE ON THE API SHAPE: this is react-joyride v3, whose prop names differ from
// the v2 ones most examples (and this file's first version) use. v3 silently
// ignores unknown props, so a v2-shaped config compiles, renders, and quietly
// does nothing. The differences that bite:
//   callback -> onEvent            (v2's name never fires; nothing advances)
//   showProgress/showSkipButton    -> options.showProgress / options.buttons
//   disableOverlayClose            -> options.overlayClickAction: false
//   disableBeacon (step)           -> options.skipBeacon
//   styles={{options:{...}}}       -> options: {backgroundColor, textColor, ...}
//   disableScrollParentFix         -> gone
// Check dist/index.d.mts before adding a prop from a blog post.

// How long Joyride waits for a step's target to appear before giving up and
// moving on. Generous because a cross-page step has to wait out the new page's
// data fetch -- every list page renders a spinner before its content.
const TARGET_WAIT_MS = 6000;

// useIsDesktop (hooks/useMediaQuery.js) is Tailwind's `lg` breakpoint, which is
// what gates the two nav surfaces the tour points at (`hidden lg:flex` sidebar
// vs `lg:hidden` bottom bar). It must stay *live*, not sampled once at mount:
// whichever surface the tour isn't pointing at is `display: none`, and a step
// whose target is invisible has no tooltip to show -- so a stale value doesn't
// degrade the tour, it deletes every nav step from it.

// Each step declares the route it belongs to and navigates there in its own
// `before` hook. Targets are chosen to be present regardless of whether the CRM
// has any data yet, which rules out the per-page sort/group controls (rendered
// only when `items.length > 0`) and the `hidden sm:block` status filters. The
// create buttons only exist for non-guests, so those steps are dropped rather
// than left to fail for a read-only user.
function buildSteps({ t, user, desktop, navigateRef, tvars }) {
  const writable = can.write(user);
  const seesReports = user?.role === "admin" || user?.role === "manager";
  const step = (route, target, key, extra) => ({
    route,
    target,
    // tvars is passed to every step's strings rather than to the handful that
    // need it: i18next ignores interpolation values a string doesn't use, so
    // one call shape covers the brand name and currency codes wherever the copy
    // happens to mention them, without a per-key special case here.
    title: t(`onboarding.${key}Title`, tvars),
    content: t(`onboarding.${key}Body`, tvars),
    // Joyride awaits this promise before showing the step, so it's the hook that
    // makes a cross-page tour possible: point the router at the step's own page
    // and let Joyride's own target polling wait out that page's render+fetch.
    // Runs on Back too, so walking backwards navigates as well.
    before: async () => {
      if (window.location.pathname !== route) navigateRef.current(route);
    },
    ...(target === "body" ? { placement: "center" } : null),
    ...extra,
  });

  const steps = [
    step("/", "body", "welcome"),
    step("/", '[data-testid="dashboard-metrics"]', "metrics"),
    step("/", '[data-testid="ai-command-input"]', "ai"),
    step("/", '[data-testid="dashboard-pipeline-chart"]', "pipeline"),
    step("/", '[data-testid="dashboard-upcoming-tasks"]', "tasks"),
    desktop
      ? step("/", '[data-testid="nav-sidebar"]', "nav")
      : step("/", '[data-testid="nav-mobile-bar"]', "navMobile"),
  ];
  // Companies and Activities have no room in the mobile bottom bar, so on a
  // phone the tour has to point out where they went before it walks to them.
  if (!desktop) steps.push(step("/", '[data-testid="nav-mobile-more"]', "more"));

  if (writable) steps.push(step("/contacts", '[data-testid="add-contact-btn"]', "contactsAdd"));
  steps.push(step("/contacts", '[data-testid="contact-search"]', "contactsFind"));

  if (writable) steps.push(step("/companies", '[data-testid="add-company-btn"]', "companiesAdd"));
  steps.push(step("/companies", '[data-testid="companies-view-grid"]', "companiesViews"));

  if (writable) steps.push(step("/deals", '[data-testid="add-deal-btn"]', "dealsAdd"));
  steps.push(step("/deals", '[data-testid="deals-view-board"]', "dealsBoard"));
  steps.push(step("/deals", '[data-testid="unassigned-filter"]', "dealsInbox"));

  steps.push(step("/projects", writable ? '[data-testid="add-project-btn"]' : "body", "projects"));
  steps.push(step("/activities", writable ? '[data-testid="add-activity-btn"]' : "body", "activities"));
  steps.push(step("/calendar", '[data-testid="calendar-grid"]', "calendar"));
  if (seesReports) steps.push(step("/utilization", '[data-testid="util-total-hours"]', "reports"));

  steps.push(step("/", desktop ? '[data-testid="notif-bell-desktop"]' : '[data-testid="notif-bell-mobile"]', "notif"));
  steps.push(step("/", desktop ? '[data-testid="theme-toggle"]' : '[data-testid="theme-toggle-mobile"]', "theme"));
  steps.push(step("/", "body", "closing"));

  return steps;
}

export default function OnboardingTour() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const desktop = useIsDesktop();

  const [run, setRun] = useState(false);
  const autoStartedRef = useRef(false);
  const completingRef = useRef(false);

  // Through a ref so the step list -- and with it Joyride's `steps` prop --
  // doesn't get rebuilt if useNavigate hands back a new function identity.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const tvars = useMemo(
    () => ({
      brand: branding.org_name,
      currencies: branding.currencies.join(" / "),
      currency: branding.default_currency,
    }),
    [branding.org_name, branding.currencies, branding.default_currency]
  );

  const steps = useMemo(
    () => buildSteps({ t, user, desktop, navigateRef, tvars }),
    [t, user, desktop, tvars]
  );

  const startTour = useCallback(() => {
    completingRef.current = false;
    setRun(true);
  }, []);

  const endTour = useCallback(async () => {
    setRun(false);
    // The end can be reported by more than one event for a single click, and
    // `user` won't have refreshed in between -- so guard the POST with a ref
    // rather than with onboarding_completed alone.
    if (user && !user.onboarding_completed && !completingRef.current) {
      completingRef.current = true;
      try {
        const res = await api.post("/auth/me/complete-onboarding");
        setUser(res.data);
      } catch (e) {
        completingRef.current = false;
        console.error("Failed to mark onboarding complete:", e);
      }
    }
  }, [user, setUser]);

  // Auto-start once, the first time a never-onboarded user shows up. The first
  // step's `before` hook sends them to the dashboard if they're elsewhere.
  useEffect(() => {
    if (!user || user.onboarding_completed || autoStartedRef.current) return;
    autoStartedRef.current = true;
    startTour();
  }, [user, startTour]);

  // Manual replay: Layout's "Replay tour" button navigates to /?tour=replay from
  // anywhere in the app -- pick that up here and force-start regardless of
  // onboarding_completed, then strip the query param so a refresh doesn't
  // re-trigger it.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("tour") !== "replay") return;
    autoStartedRef.current = true;
    navigate(location.pathname, { replace: true });
    startTour();
  }, [location.search, location.pathname, navigate, startTour]);

  const handleEvent = useCallback((data) => {
    const { status, step, type } = data;
    // Joyride skips an unreachable step on its own (after TARGET_WAIT_MS) but
    // says nothing unless `debug` is on -- surface it, because a quietly
    // shortened tour reads as a design decision instead of a bug.
    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn("[tour] target missing or hidden, skipped step:", step?.target);
      return;
    }
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) endTour();
  }, [endTour]);

  if (!user || location.pathname === "/login") return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      // Explicit so a replay always restarts from the top: Joyride's start()
      // keeps the current index when it isn't given a number, which would make
      // a second replay resume on the last step of the first one.
      initialStepIndex={0}
      continuous
      scrollToFirstStep
      debug={process.env.NODE_ENV === "development"}
      onEvent={handleEvent}
      locale={{
        back: t("onboarding.back"),
        close: t("onboarding.close"),
        last: t("onboarding.finish"),
        next: t("onboarding.next"),
        skip: t("onboarding.skip"),
      }}
      options={{
        // 'skip' in place of v3's default 'close' button, whose default action
        // is to advance a step rather than leave the tour.
        buttons: ["back", "skip", "primary"],
        showProgress: true,
        skipBeacon: true,
        // A 20-step cross-page tour is far too easy to lose to a stray click on
        // the backdrop.
        overlayClickAction: false,
        targetWaitTimeout: TARGET_WAIT_MS,
        arrowColor: "var(--surface)",
        backgroundColor: "var(--surface)",
        overlayColor: "rgba(6, 11, 25, 0.7)",
        primaryColor: branding.primary,
        textColor: "var(--txt)",
        zIndex: 10000,
      }}
    />
  );
}
